/**
 * This directive, `HoveredLeftMenuDirective`, is designed to create a left-side menu
 * that appears when a user hovers over a designated button. It offers the following functionality:
 *
 * - The directive can be applied to any element and accepts an Angular template reference for
 *   the menu's content via the `hoverMenuTemplate` input.
 * - It provides customizable menu options such as auto-closing behavior, menu width,
 *   CSS classes, and positioning adjustments through the `menuOptions` input.
 * - The directive tracks the user's hover state over both the button and the menu.
 *   It ensures the menu appears when hovering over the button and, depending on settings,
 *   can remain open when hovering over the menu itself.
 * - The directive dynamically creates and appends the menu to the DOM, ensuring proper positioning
 *   relative to the host element.
 * - It supports multiple menus and clears previously open menus to avoid overlapping or duplicate menus.
 *
 * Usage:
 * - To use this directive, apply `[zHoveredLeftMenu]` to the element and bind an Angular `ng-template`
 *   for the menu content.
 */

import {
  Directive,
  effect,
  ElementRef,
  HostListener,
  input,
  Input,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';

export interface MenuOptions {
  autoClose?: boolean;
  menuWidth: string;
  cssClass?: string;
  topSpace?: string;
}
@Directive({
  selector: '[zHoveredLeftMenu]',
  standalone: true,
})
export class HoveredLeftMenuDirective {
  @Input() hoverMenuTemplate!: TemplateRef<unknown> | undefined | null;
  @Input() menuOptions: MenuOptions = {
    autoClose: true,
    menuWidth: '0px',
  };

  forceHide = input(false);

  private menuView: any; // Store the embedded view
  private isHoveringButton = false; // Track if hovering over the button
  private isHoveringMenu = false; // Track if hovering over the menu
  private previousMouseX = 0; // Track the mouse position to use it when user move to left menu after hover on button

  // Static array to track open menus
  private static openMenus: {
    menuElement: HTMLElement;
    viewContainer: ViewContainerRef;
    menuView: any;
  }[] = [];

  constructor(
    private readonly viewContainer: ViewContainerRef,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
  ) {
    effect(() => {
      if (this.forceHide()) {
        this.hideMenu();
      }
    });
  }

  // Mouse enters the button
  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: MouseEvent) {
    // To check if template is defined
    if (!this.hoverMenuTemplate) {
      return;
    }
    this.isHoveringButton = true;
    this.previousMouseX = event.clientX; // Store the mouse X position
    this.showMenu();
  }

  // Mouse leaves the button
  @HostListener('mouseleave', ['$event'])
  onMouseLeave(event: MouseEvent) {
    const hostElement = this.elementRef.nativeElement;
    const buttonRightEdge = hostElement.getBoundingClientRect().right;

    // Check if the mouse is moving to the right (towards the menu)
    if (
      event.clientX >= buttonRightEdge &&
      event.clientX > this.previousMouseX
    ) {
      // If mouse leaves from the right, don't close the menu
      return;
    }
    this.isHoveringButton = false;
    this.checkIfShouldHideMenu();
  }

  // Show the menu
  private showMenu() {
    this.removeAllOpenMenus();

    if (!this.hoverMenuTemplate) return;

    this.menuView = this.viewContainer.createEmbeddedView(
      this.hoverMenuTemplate,
    );
    const hoveredMenuContainer = this.createMenuContainer(this.menuView);
    const hostElement = this.elementRef.nativeElement;

    // Append the menu to the body or a container to ensure it's rendered correctly
    document.body.appendChild(hoveredMenuContainer);

    // Set styles for positioning
    const menuElement = hoveredMenuContainer as HTMLElement;
    if (menuElement) {
      menuElement.style.position = 'absolute';
      menuElement.style.top = `64px`;
      menuElement.style.bottom = `16px`;
      menuElement.style.left = `${hostElement.getBoundingClientRect().left + window.scrollX + 56}px`;
      menuElement.style.zIndex = '1000';
      menuElement.style.borderRadius = '16px';
      menuElement.style.width = this.menuOptions.menuWidth;
    }

    /**
     * Store the left menu data : menu html, ViewContainer and the embedded view
     * to remove them when create a new menu.
     */
    HoveredLeftMenuDirective.openMenus.push({
      menuElement,
      viewContainer: this.viewContainer,
      menuView: this.menuView,
    });

    // Track mouse events on the menu if the option is enabled
    if (!this.menuOptions.autoClose) {
      this.renderer.listen(menuElement, 'mouseenter', () =>
        this.onMenuMouseEnter(),
      );
      this.renderer.listen(menuElement, 'mouseleave', () =>
        this.onMenuMouseLeave(),
      );
    }
  }

  /**
   * Generate the html container of hovered menu
   * @param menuView Embedded View created from ng-template
   * @returns the html of hovered menu
   */
  private createMenuContainer(menuView: any) {
    const hostElement = this.elementRef.nativeElement;

    const hoveredMenuContainer = this.renderer.createElement('div');
    this.renderer.addClass(hoveredMenuContainer, 'st-hovered-menu');

    const hoveredMenuBody = this.renderer.createElement('div');
    this.renderer.setStyle(hoveredMenuBody, 'overflow', 'auto');
    this.renderer.addClass(hoveredMenuBody, 'st-hovered-menu-body');
    this.renderer.appendChild(hoveredMenuBody, menuView.rootNodes[0]);

    const triangleBody = this.renderer.createElement('div');
    this.renderer.addClass(triangleBody, 'st-triangle-body');
    const triangle: HTMLDivElement = this.renderer.createElement('div');
    triangle.style.top = `${hostElement.getBoundingClientRect().top + window.scrollY - 50}px`;

    this.renderer.addClass(triangle, 'st-triangle');
    this.renderer.appendChild(triangleBody, triangle);

    this.renderer.insertBefore(
      hoveredMenuContainer,
      triangleBody,
      hoveredMenuContainer.firstChild,
    );
    this.renderer.appendChild(hoveredMenuContainer, hoveredMenuBody);

    return hoveredMenuContainer;
  }

  // Mouse enters the menu
  private onMenuMouseEnter() {
    this.isHoveringMenu = true;
  }

  // Mouse leaves the menu
  private onMenuMouseLeave() {
    this.isHoveringButton = false;
    this.isHoveringMenu = false;
    this.checkIfShouldHideMenu();
  }

  // Check if both the button and the menu are not hovered
  private checkIfShouldHideMenu(): void {
    const { autoClose } = this.menuOptions;
    const shouldHide =
      (autoClose && !this.isHoveringButton) ||
      (!autoClose && !this.isHoveringButton && !this.isHoveringMenu);

    if (shouldHide) {
      this.hideMenu();
    }
  }

  // Hide the menu
  private hideMenu() {
    const hoveredMenuContainer = document.querySelector('.st-hovered-menu');
    if (hoveredMenuContainer) {
      this.viewContainer.clear();
      this.menuView = null;
      hoveredMenuContainer.remove();
    }
  }

  //remove All opened Menus
  private removeAllOpenMenus() {
    this.viewContainer.clear();
    this.menuView = null;
    HoveredLeftMenuDirective.openMenus.forEach(menu => {
      menu.menuElement.remove(); // Remove the menu from the DOM
      menu.viewContainer.clear(); // Clear the viewContainer
      menu.menuView = null;
    });

    HoveredLeftMenuDirective.openMenus = [];
  }
}
